import { useEffect, useState, useRef } from 'react';
import { Modal } from '@mui/material';
import { toast } from 'react-toastify';

const SettingsModal = ({
  isOpen,
  onClose,
  onSave,
  configKey,
  configData,
  isSubmitting,
}) => {
  const [description, setDescription] = useState('');
  const [arrayData, setArrayData] = useState({}); // { arrayKey: [items] }
  const [editingItem, setEditingItem] = useState(null); // { arrayKey, index, data }
  const [newItemData, setNewItemData] = useState({}); // { arrayKey: { field: value } }

  // Error states for inline validation messages
  const [addErrors, setAddErrors] = useState({}); // { arrayKey: 'error message' }
  const [editError, setEditError] = useState(''); // error message for editing item

  // Store original data for cancel/reset
  const originalDataRef = useRef({ description: '', arrayData: {} });

  // Parse the config data structure
  useEffect(() => {
    if (isOpen && configData) {
      let desc = '';
      let arrays = {};

      if (typeof configData === 'object' && !Array.isArray(configData)) {
        // Extract description
        desc = configData.description || '';

        // Extract all array properties (deep clone to preserve original)
        Object.entries(configData).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            arrays[key] = JSON.parse(JSON.stringify(value));
          }
        });
      } else if (Array.isArray(configData)) {
        // Handle direct array case
        arrays = { items: JSON.parse(JSON.stringify(configData)) };
      }

      setDescription(desc);
      setArrayData(arrays);

      // Store original data for cancel
      originalDataRef.current = {
        description: desc,
        arrayData: JSON.parse(JSON.stringify(arrays)),
      };

      setEditingItem(null);
      setNewItemData({});
      setAddErrors({});
      setEditError('');
    }
  }, [isOpen, configData]);

  // Cancel - restore original data and close
  const handleClose = () => {
    // Reset to original state
    setDescription(originalDataRef.current.description);
    setArrayData(JSON.parse(JSON.stringify(originalDataRef.current.arrayData)));
    setEditingItem(null);
    setNewItemData({});
    setAddErrors({});
    setEditError('');
    onClose();
  };

  // Get the fields from items in an array (by looking at first item)
  const getFieldsForArray = (arrayKey) => {
    const items = arrayData[arrayKey] || [];
    if (items.length > 0 && typeof items[0] === 'object') {
      return Object.keys(items[0]);
    }
    return ['value']; // Default for simple arrays
  };

  // Check if an item is a duplicate (case-insensitive comparison)
  const isDuplicate = (arrayKey, newItem, excludeIndex = -1) => {
    const items = arrayData[arrayKey] || [];
    return items.some((item, index) => {
      if (index === excludeIndex) return false; // Skip the item being edited

      if (typeof item === 'object' && typeof newItem === 'object') {
        // Compare all fields (case-insensitive for strings)
        return Object.keys(newItem).every((key) => {
          const newVal = String(newItem[key] || '').toLowerCase().trim();
          const existingVal = String(item[key] || '').toLowerCase().trim();
          return newVal === existingVal;
        });
      }
      return String(item).toLowerCase().trim() === String(newItem).toLowerCase().trim();
    });
  };

  // Handle adding a new item to an array
  const handleAddItem = (arrayKey) => {
    const itemData = newItemData[arrayKey] || {};
    const fields = getFieldsForArray(arrayKey);

    // Check if at least one field has a value
    const hasValue = fields.some(field => itemData[field]?.trim());
    if (!hasValue) return;

    // Create new item
    const newItem = {};
    fields.forEach(field => {
      newItem[field] = (itemData[field] || '').trim();
    });

    // Check for duplicates
    if (isDuplicate(arrayKey, newItem)) {
      setAddErrors(prev => ({ ...prev, [arrayKey]: 'This item already exists' }));
      return;
    }

    // Clear error and add item
    setAddErrors(prev => ({ ...prev, [arrayKey]: '' }));

    setArrayData(prev => ({
      ...prev,
      [arrayKey]: [...(prev[arrayKey] || []), newItem],
    }));

    // Clear new item form
    setNewItemData(prev => ({
      ...prev,
      [arrayKey]: {},
    }));
  };

  // Handle deleting an item
  const handleDeleteItem = (arrayKey, index) => {
    const items = arrayData[arrayKey] || [];

    // Prevent deleting the last item
    if (items.length <= 1) {
      toast.warning('Cannot delete the last item. At least one item is required');
      return;
    }

    setArrayData(prev => ({
      ...prev,
      [arrayKey]: prev[arrayKey].filter((_, i) => i !== index),
    }));
  };

  // Handle starting edit
  const handleStartEdit = (arrayKey, index, item) => {
    setEditingItem({
      arrayKey,
      index,
      data: { ...item },
    });
  };

  // Handle saving edit
  const handleSaveEdit = () => {
    if (!editingItem) return;

    const { arrayKey, index, data } = editingItem;

    // Trim values
    const trimmedData = {};
    Object.keys(data).forEach(key => {
      trimmedData[key] = (data[key] || '').trim();
    });

    // Check for duplicates (exclude current item being edited)
    if (isDuplicate(arrayKey, trimmedData, index)) {
      setEditError('This item already exists');
      return;
    }

    // Clear error and save
    setEditError('');

    setArrayData(prev => ({
      ...prev,
      [arrayKey]: prev[arrayKey].map((item, i) =>
        i === index ? trimmedData : item
      ),
    }));
    setEditingItem(null);
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditError('');
  };

  // Update editing item field
  const updateEditingField = (field, value) => {
    // Clear error when user starts typing
    if (editError) setEditError('');

    setEditingItem(prev => ({
      ...prev,
      data: {
        ...prev.data,
        [field]: value,
      },
    }));
  };

  // Update new item field
  const updateNewItemField = (arrayKey, field, value) => {
    // Clear error when user starts typing
    if (addErrors[arrayKey]) {
      setAddErrors(prev => ({ ...prev, [arrayKey]: '' }));
    }

    setNewItemData(prev => ({
      ...prev,
      [arrayKey]: {
        ...(prev[arrayKey] || {}),
        [field]: value,
      },
    }));
  };

  // Build the data structure to save
  const handleSubmit = (e) => {
    e.preventDefault();

    const result = {
      description,
      ...arrayData,
    };

    onSave(configKey, result);
  };

  const formatTypeName = (key) => {
    if (!key) return '';
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  const formatFieldName = (field) => {
    return field
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Get display value for an item
  const getItemDisplayValue = (item) => {
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
      // Return first non-id field value, or concatenate key fields
      const displayFields = Object.entries(item)
        .filter(([key]) => !key.toLowerCase().includes('id'))
        .map(([, value]) => value);
      return displayFields.join(' - ') || JSON.stringify(item);
    }
    return String(item);
  };

  // Count total items across all arrays
  const getTotalItemCount = () => {
    return Object.values(arrayData).reduce((sum, arr) => sum + arr.length, 0);
  };

  // Check if there are any changes compared to original data
  const hasChanges = () => {
    // Check description change
    if (description !== originalDataRef.current.description) {
      return true;
    }

    // Check array data changes
    const originalArrayData = originalDataRef.current.arrayData;
    const currentArrayKeys = Object.keys(arrayData);
    const originalArrayKeys = Object.keys(originalArrayData);

    // Check if number of arrays changed
    if (currentArrayKeys.length !== originalArrayKeys.length) {
      return true;
    }

    // Check each array for changes
    for (const key of currentArrayKeys) {
      const currentArray = arrayData[key] || [];
      const originalArray = originalArrayData[key] || [];

      // Check if array length changed
      if (currentArray.length !== originalArray.length) {
        return true;
      }

      // Deep compare each item
      for (let i = 0; i < currentArray.length; i++) {
        const currentItem = currentArray[i];
        const originalItem = originalArray[i];

        if (JSON.stringify(currentItem) !== JSON.stringify(originalItem)) {
          return true;
        }
      }
    }

    return false;
  };

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
        <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full md:w-[800px] max-w-4xl max-h-[90vh] overflow-y-auto border border-gray-200 relative">
          {/* Close X Button */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold p-1"
            aria-label="Close"
          >
            &times;
          </button>

          {/* Modal Title */}
          <h2 className="text-2xl font-bold text-secondary-800 mb-1">
            Edit {formatTypeName(configKey)}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {getTotalItemCount()} total items in {Object.keys(arrayData).length} group(s)
          </p>

          <form onSubmit={handleSubmit}>
            {/* Description Field */}
            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-2">
                Description
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 outline-none focus:border-secondary-500"
                placeholder="Enter description..."
              />
            </div>

            {/* Array Sections */}
            {Object.entries(arrayData).map(([arrayKey, items]) => {
              const fields = getFieldsForArray(arrayKey);

              return (
                <div key={arrayKey} className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
                  {/* Array Header */}
                  <div className="bg-gray-100 px-4 py-3 flex justify-between items-center">
                    <span className="font-medium text-gray-700">
                      {formatTypeName(arrayKey)} ({items.length} items)
                    </span>
                  </div>

                  {/* Items Table */}
                  <div className="max-h-60 overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {fields.map(field => (
                            <th key={field} className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                              {formatFieldName(field)}
                            </th>
                          ))}
                          <th className="text-center py-2 px-3 text-sm font-semibold text-gray-700 w-24">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.length === 0 ? (
                          <tr>
                            <td colSpan={fields.length + 1} className="py-4 text-center text-gray-500">
                              No items
                            </td>
                          </tr>
                        ) : (
                          items.map((item, index) => {
                            const isEditing = editingItem?.arrayKey === arrayKey && editingItem?.index === index;

                            return (
                              <tr key={index} className="border-t border-gray-100 hover:bg-gray-50">
                                {isEditing ? (
                                  <>
                                    {fields.map((field, fieldIndex) => (
                                      <td key={field} className="py-2 px-3">
                                        <div>
                                          <input
                                            type="text"
                                            value={editingItem.data[field] || ''}
                                            onChange={(e) => updateEditingField(field, e.target.value)}
                                            className={`w-full border rounded px-2 py-1 text-sm outline-none ${
                                              editError
                                                ? 'border-red-500 bg-red-50'
                                                : 'border-gray-300 focus:border-secondary-500'
                                            }`}
                                          />
                                          {/* Show error only on first field */}
                                          {editError && fieldIndex === 0 && (
                                            <p className="text-red-500 text-xs mt-1">{editError}</p>
                                          )}
                                        </div>
                                      </td>
                                    ))}
                                    <td className="py-2 px-3 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          type="button"
                                          onClick={handleSaveEdit}
                                          className="text-green-600 hover:text-green-800 text-xs px-2 py-1"
                                        >
                                          Save
                                        </button>
                                        <button
                                          type="button"
                                          onClick={handleCancelEdit}
                                          className="text-gray-500 hover:text-gray-700 text-xs px-2 py-1"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    {fields.map(field => (
                                      <td key={field} className="py-2 px-3 text-sm text-gray-700">
                                        {typeof item === 'object' ? item[field] : item}
                                      </td>
                                    ))}
                                    <td className="py-2 px-3 text-center">
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          type="button"
                                          onClick={() => handleStartEdit(arrayKey, index, item)}
                                          className="text-secondary-600 hover:text-secondary-800 text-xs px-2 py-1"
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteItem(arrayKey, index)}
                                          disabled={items.length <= 1}
                                          className={`text-xs px-2 py-1 ${
                                            items.length <= 1
                                              ? 'text-gray-300 cursor-not-allowed'
                                              : 'text-red-500 hover:text-red-700'
                                          }`}
                                          title={items.length <= 1 ? 'At least one item required' : 'Delete'}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Add New Item */}
                  <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      {fields.map(field => (
                        <input
                          key={field}
                          type="text"
                          value={newItemData[arrayKey]?.[field] || ''}
                          onChange={(e) => updateNewItemField(arrayKey, field, e.target.value)}
                          placeholder={formatFieldName(field)}
                          className={`flex-1 border rounded px-3 py-1.5 text-sm outline-none ${
                            addErrors[arrayKey]
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-300 focus:border-secondary-500'
                          }`}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleAddItem(arrayKey);
                            }
                          }}
                        />
                      ))}
                      <button
                        type="button"
                        onClick={() => handleAddItem(arrayKey)}
                        className="px-4 py-1.5 rounded-full text-sm font-medium bg-secondary-700 text-white hover:bg-secondary-800 transition"
                      >
                        Add
                      </button>
                    </div>
                    {/* Error message below inputs */}
                    {addErrors[arrayKey] && (
                      <p className="text-red-500 text-xs mt-2">{addErrors[arrayKey]}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="border border-secondary-700 text-secondary-700 py-2 px-6 rounded-full hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !hasChanges()}
                className="bg-secondary-700 text-white py-2 px-8 rounded-full shadow hover:bg-secondary-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default SettingsModal;
